import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Badge,
  Box,
  Button,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { SortOrder } from '@duke-hq/lib/pagination';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { withPermission } from '../withPermission';
import { LockedBadge } from './LockedBadge';
import { testIds } from './testIds';

/**
 * Every email sent on this organisation's behalf, grouped by family: a flat list
 * would hide which rows are the same email said differently.
 */

type Variant = {
  template: string;
  /** Set on a family's variant: the condition that picks it out of its siblings. */
  condition?: string;
  /** Set on a standalone email: its own name, since it has no family to head it. */
  label?: string;
  /** Set on a standalone email: what causes it to be sent. A family carries its own. */
  whenSent?: string;
  edited: boolean;
  locked?: 'codeOwned';
};

function StatusCell({ variant }: { variant: Variant }): React.ReactElement {
  if (variant.locked === 'codeOwned') {
    return <LockedBadge />;
  }
  return variant.edited ? (
    <Badge colorScheme="green">
      <Trans>Edited</Trans>
    </Badge>
  ) : (
    <Badge colorScheme="gray">
      <Trans>Default wording</Trans>
    </Badge>
  );
}

function TemplateLink({ variant }: { variant: Variant }): React.ReactElement {
  return (
    <Link as={RouterLink} to={`/templates/${variant.template}`} data-testid={testIds.templateLink}>
      {variant.condition ?? variant.label ?? variant.template}
    </Link>
  );
}

function FamilyRows({
  label,
  whenSent,
  variants,
}: {
  label: string;
  whenSent: string;
  variants: Variant[];
}): React.ReactElement {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/*
        The whole row toggles, since the name is what a reader aims at rather than
        the chevron beside it. Still a button, because expanding a family is the
        only way to reach most of the templates and has to work without a mouse --
        the row is the button's hit area rather than a click handler on the cell.
      */}
      <Tr
        _hover={{ bg: 'bg.subtle' }}
        cursor="pointer"
        onClick={() => setOpen(!open)}
        data-testid={testIds.familyRow}
      >
        <Td py={4}>
          <Button
            onClick={(event) => {
              // The row already toggles; without this the button's own click
              // bubbles to it and toggles a second time, so it never opens.
              event.stopPropagation();
              setOpen(!open);
            }}
            aria-expanded={open}
            variant="unstyled"
            display="inline-flex"
            alignItems="center"
            gap={2}
            fontWeight="medium"
            height="auto"
          >
            {open ? <ChevronDown size={iconSize.sm} /> : <ChevronRight size={iconSize.sm} />}
            <Text as="span">{label}</Text>
          </Button>
        </Td>
        <Td py={4}>
          <Text color="fg.muted" fontSize="sm">
            {whenSent}
          </Text>
        </Td>
        <Td py={4}>
          <Text color="fg.muted" fontSize="sm">
            <Plural value={variants.length} one="# version" other="# versions" />
          </Text>
        </Td>
      </Tr>
      {open
        ? variants.map((variant) => (
            <Tr key={variant.template} data-testid={testIds.variantRow}>
              <Td py={4} pl={10}>
                <TemplateLink variant={variant} />
              </Td>
              {/*
                Blank: the condition is what the link already says, and the family
                row above states the trigger every variant shares. Repeating either
                here would fill the column without telling a reader anything.
              */}
              <Td py={4} />
              <Td py={4}>
                <StatusCell variant={variant} />
              </Td>
            </Tr>
          ))
        : null}
    </>
  );
}

/**
 * Families and standalone emails share one column, so they sort together: a reader
 * looking for a name does not know which of the two it is.
 */
type Row =
  | { kind: 'family'; name: string; whenSent: string; variants: Variant[] }
  | { kind: 'standalone'; name: string; variant: Variant };

function TemplatesListPageContent(): React.ReactElement {
  const { data, isLoading } = trpcAdmin.emailTemplates.list.useQuery();
  const [sortOrder, setSortOrder] = useState(SortOrder.ASC);

  if (isLoading || data == null) {
    return (
      <Box p={4}>
        <Spinner />
      </Box>
    );
  }

  const rows: Row[] = [
    ...data.families.map(
      (family): Row => ({
        kind: 'family',
        name: family.label,
        whenSent: family.whenSent,
        variants: family.variants,
      }),
    ),
    ...data.standalone.map(
      (variant): Row => ({
        kind: 'standalone',
        name: variant.label ?? variant.template,
        variant,
      }),
    ),
  ].sort((a, b) => (sortOrder === SortOrder.ASC ? 1 : -1) * a.name.localeCompare(b.name));

  return (
    <TableViewLayout
      pageHeader={
        <PageHeading>
          <Trans>Email templates</Trans>
        </PageHeading>
      }
      tableActionsLeft={
        <Text fontSize="md" fontWeight="semibold" color="fg.subtle" data-testid={testIds.count}>
          {/* The leaves, not the rows: a family is one line here and several emails. */}
          <Plural value={data.total} one="# email" other="# emails" />
        </Text>
      }
      table={
        <Table variant="simple" data-testid={testIds.table}>
          <Thead>
            <Tr>
              <Th>
                {/* One sortable column, so it is always the one being sorted by. */}
                <SortableHeader
                  label={t`Email`}
                  columnName="name"
                  currentOrderBy="name"
                  currentSortOrder={sortOrder}
                  onSortChange={() =>
                    setSortOrder(sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC)
                  }
                />
              </Th>
              <Th>
                <Trans>When it's sent</Trans>
              </Th>
              <Th>
                <Trans>Wording</Trans>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) =>
              row.kind === 'family' ? (
                <FamilyRows
                  key={row.name}
                  label={row.name}
                  whenSent={row.whenSent}
                  variants={row.variants}
                />
              ) : (
                <Tr key={row.variant.template} data-testid={testIds.standaloneRow}>
                  <Td py={4}>
                    <TemplateLink variant={row.variant} />
                  </Td>
                  <Td py={4}>
                    <Text color="fg.muted" fontSize="sm">
                      {row.variant.whenSent}
                    </Text>
                  </Td>
                  <Td py={4}>
                    <StatusCell variant={row.variant} />
                  </Td>
                </Tr>
              ),
            )}
          </Tbody>
        </Table>
      }
    />
  );
}

export const TemplatesListPage = withPermission('canManageTemplates', TemplatesListPageContent);
